import { useMemo } from 'react';
import './PixelScenes.css';

// Tiny fish for the aquarium
const PixelFish = ({
  color = '#ff6b35',
  size = 'small',
  delay = 0
}: {
  color?: string;
  size?: 'tiny' | 'small';
  delay?: number;
}) => {
  const sizeClass = size === 'tiny' ? 'fish-tiny' : 'fish-small';

  return (
    <div
      className={`pixel-fish ${sizeClass}`}
      style={{
        '--fish-color': color,
        animationDelay: `${delay}s`
      } as React.CSSProperties}
    >
      <div className="fish-body"></div>
      <div className="fish-tail"></div>
      <div className="fish-eye"></div>
      <div className="fish-fin"></div>
    </div>
  );
};

// Mini Aquarium Diorama
export const PixelAquarium = () => {
  const fish = useMemo(() => [
    { color: '#ff6b35', size: 'small' as const, delay: 0 },
    { color: '#00d9ff', size: 'tiny' as const, delay: 1.5 },
    { color: '#39ff14', size: 'tiny' as const, delay: 0.8 },
  ], []);

  return (
    <div className="pixel-aquarium">
      {/* Glass tank */}
      <div className="aquarium-glass">
        {/* Water */}
        <div className="aquarium-water">
          {/* Bubbles */}
          <div className="bubble bubble-1"></div>
          <div className="bubble bubble-2"></div>
          <div className="bubble bubble-3"></div>

          {/* Fish */}
          {fish.map((f, i) => (
            <PixelFish key={i} {...f} />
          ))}

          {/* Seaweed */}
          <div className="seaweed seaweed-1"></div>
          <div className="seaweed seaweed-2"></div>

          {/* Gravel */}
          <div className="gravel"></div>
        </div>
      </div>
      {/* Stand */}
      <div className="aquarium-stand"></div>
    </div>
  );
};

// Mini Forest Scene
export const PixelForest = () => {
  return (
    <div className="pixel-forest">
      <div className="tree tree-1">
        <div className="tree-top"></div>
        <div className="tree-trunk"></div>
      </div>
      <div className="tree tree-2">
        <div className="tree-top"></div>
        <div className="tree-trunk"></div>
      </div>
      <div className="tree tree-3">
        <div className="tree-top"></div>
        <div className="tree-trunk"></div>
      </div>
      <div className="forest-ground"></div>
      {/* Little mushrooms */}
      <div className="mushroom mushroom-1"></div>
      <div className="mushroom mushroom-2"></div>
    </div>
  );
};

// Mini Beach Scene
export const PixelBeach = () => {
  return (
    <div className="pixel-beach">
      {/* Sun */}
      <div className="beach-sun"></div>
      {/* Waves */}
      <div className="wave wave-1"></div>
      <div className="wave wave-2"></div>
      {/* Sand */}
      <div className="beach-sand">
        <div className="sand-texture"></div>
      </div>
      {/* Palm tree */}
      <div className="palm-tree">
        <div className="palm-trunk"></div>
        <div className="palm-frond palm-frond-1"></div>
        <div className="palm-frond palm-frond-2"></div>
        <div className="palm-frond palm-frond-3"></div>
      </div>
      {/* Starfish */}
      <div className="starfish"></div>
    </div>
  );
};

// Mini Mycelium Network (fits the patent theme!)
export const PixelMycelium = () => {
  return (
    <div className="pixel-mycelium">
      {/* Main mushroom */}
      <div className="main-mushroom">
        <div className="mushroom-cap"></div>
        <div className="mushroom-stem"></div>
        <div className="mushroom-glow"></div>
      </div>
      {/* Network threads */}
      <div className="mycelium-thread thread-1"></div>
      <div className="mycelium-thread thread-2"></div>
      <div className="mycelium-thread thread-3"></div>
      {/* Data pulses */}
      <div className="data-pulse pulse-1"></div>
      <div className="data-pulse pulse-2"></div>
      {/* Small mushrooms */}
      <div className="small-mushroom sm-1"></div>
      <div className="small-mushroom sm-2"></div>
      {/* Ground */}
      <div className="mycelium-ground"></div>
    </div>
  );
};

export type SceneType = 'aquarium' | 'forest' | 'beach' | 'mycelium';

interface PixelSceneProps {
  type: SceneType;
}

const PixelScene = ({ type }: PixelSceneProps) => {
  switch (type) {
    case 'aquarium':
      return <PixelAquarium />;
    case 'forest':
      return <PixelForest />;
    case 'beach':
      return <PixelBeach />;
    case 'mycelium':
      return <PixelMycelium />;
    default:
      return null;
  }
};

export default PixelScene;
